import { notify } from "@/components/common/Notification";
import SchemaFormRender from "@/components/Schema/SchemaFormRender";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { DynamicModuleService, SchemaService } from "@/services/APIs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

/** Deep clone without prototypes; drops cycles so Formily/setValues cannot recurse infinitely. */
function toPlainRecord(record: unknown): Record<string, any> {
  if (record === null || typeof record !== "object") {
    return {};
  }
  try {
    return JSON.parse(JSON.stringify(record)) as Record<string, any>;
  } catch {
    return { ...(record as Record<string, any>) };
  }
}

const SchemaFormAutoSubmit = forwardRef(
  (
    {
      form,
      schemaName,
      recordId,
    }: {
      form: any;
      schemaName?: string;
      recordId?: string;
    },
    ref
  ) => {
    const queryClient = useQueryClient();
    const lastSyncedPayloadRef = useRef<string | null>(null);
    const { data: schemaJson } = useQuery({
      queryKey: ["schema", schemaName],
      queryFn: () => SchemaService.getSchemaByName(schemaName ?? ""),
      enabled: !!schemaName,
    });
    const isEditMode = !!recordId;

    const { data: recordInfo } = useQuery({
      queryKey: ["dynamic-model", schemaName, recordId],
      queryFn: () =>
        DynamicModuleService.getItemById(schemaName ?? "", recordId ?? ""),
      enabled: isEditMode && !!schemaName && !!recordId,
    });

    const handleSave = async () => {
      try {
        // Validate form and throw error if validation fails
        await form.validate();
        const values = removeMetaFields(form.values);

        if (isEditMode) {
          // Check for changes before updating
          if (!checkFormChanges(values, recordInfo, notify)) {
            return null;
          }
          const response = await DynamicModuleService.updateItem(
            schemaName ?? "",
            recordId ?? "",
            values
          );
          if (response) {
            // Do not await: refetch + useEffect below would re-apply recordInfo while recordId
            // is still set, racing the parent’s reset after save and refilling the form.
            void queryClient.invalidateQueries({
              queryKey: ["dynamic-model", schemaName, recordId],
            });
          }
          return response;
        } else {
          const response = await DynamicModuleService.createItem(
            schemaName ?? "",
            values
          );
          return response;
        }
      } catch (error) {
        // Handle validation errors
        console.error("Form validation failed:", error);
        // notify.error("Please fill in all required fields correctly");
        throw error; // Re-throw to let the calling component handle it
      }
    };

    useImperativeHandle(ref, () => ({
      handleSave,
    }));

    useEffect(() => {
      if (!isEditMode || !recordId || !recordInfo) {
        lastSyncedPayloadRef.current = null;
        return;
      }
      const infoId = (recordInfo as any)?.id ?? (recordInfo as any)?.pk;
      if (infoId !== null && String(infoId) !== String(recordId)) {
        return;
      }
      const plain = toPlainRecord(recordInfo);
      const payload = removeMetaFields(plain);
      let fingerprint: string;
      try {
        fingerprint = `${recordId}:${JSON.stringify(payload)}`;
      } catch {
        fingerprint = `${recordId}:${String(infoId ?? "")}`;
      }
      if (lastSyncedPayloadRef.current === fingerprint) {
        return;
      }
      lastSyncedPayloadRef.current = fingerprint;
      // Use setValues (not setInitialValues) so fresh API data overrides form when editing
      form.setValues(payload);
      // `form` is stable from parent; omit from deps to avoid redundant effect runs.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, recordId, recordInfo]);

    return (
      <SchemaFormRender
        form={form}
        schemaJson={schemaJson?.json ?? schemaJson}
      />
    );
  }
);

export default SchemaFormAutoSubmit;
